import { useState, useEffect } from 'react';

/**
 * Custom hook to observe changes in localStorage for a specific key
 * @param {string} key - The localStorage key to observe
 * @param {any} initialValue - Initial value if key doesn't exist
 * @returns {any} The current value of the localStorage key
 */
export const useLocalStorageObserver = (key, initialValue = null) => {
  // Get initial value from localStorage
  const getStoredValue = () => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState(getStoredValue);

  useEffect(() => {
    // Function to handle storage changes
    const handleStorageChange = (e) => {
      if (e.key === key) {
        try {
          const newValue = e.newValue ? JSON.parse(e.newValue) : initialValue;
          setStoredValue(newValue);
        } catch (error) {
          console.error(
            `Error parsing localStorage value for key "${key}":`,
            error
          );
          setStoredValue(initialValue);
        }
      }
    };

    // Custom event handler for manual localStorage updates
    const handleCustomStorageChange = (e) => {
      if (e.detail.key === key) {
        setStoredValue(e.detail.newValue);
      }
    };

    // Listen for storage events (works across tabs)
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom events (works within same tab)
    window.addEventListener('localStorageChange', handleCustomStorageChange);

    // Polling fallback for same-tab changes (in case custom events aren't fired)
    const pollInterval = setInterval(() => {
      const currentValue = getStoredValue();
      setStoredValue((prevValue) => {
        // Only update if value actually changed
        if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
          return currentValue;
        }
        return prevValue;
      });
    }, 10); // Check every 10ms

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(
        'localStorageChange',
        handleCustomStorageChange
      );
      clearInterval(pollInterval);
    };
  }, [key, initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return storedValue;
};

/**
 * Utility function to set localStorage and trigger custom event
 * @param {string} key - The localStorage key
 * @param {any} value - The value to set
 */
export const setLocalStorageWithEvent = (key, value) => {
  try {
    const stringValue = JSON.stringify(value);
    window.localStorage.setItem(key, stringValue);

    // Dispatch custom event for same-tab detection
    window.dispatchEvent(
      new CustomEvent('localStorageChange', {
        detail: { key, newValue: value },
      })
    );
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
  }
};
